import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { toCampaignRow, toCampaignStat } from '#/entities/campaign'
import type { CampaignRow, CampaignStatFigure } from '#/entities/campaign'
import {
  useCampaignsSummaryApiV1CampaignsSummaryGet,
  useListCampaignRowsApiV1CampaignsRowsGet,
} from '#/shared/api/generated/communication/theCargoCommunication'
import { CampaignTone } from '#/shared/api/generated/communication/models'
import type { CampaignSummaryRead } from '#/shared/api/generated/communication/models'
import { isPermissionDenied } from '#/shared/lib/api/error-handler'
import { useDebouncedValue } from '#/shared/lib/hooks'
import type { DataTablePagination } from '#/shared/ui/data-table'

import {
  CAMPAIGN_DEFAULT_TAB,
  CAMPAIGN_TAB_ORDER,
  campaignTabLabelKey,
} from './tabs'
import type { CampaignTabKey } from './tabs'

/** The window the toolbar and the pagination bar both page through. */
const WINDOW_SIZE = 100
/** Typing pause before the search reaches the service. */
const SEARCH_DEBOUNCE_MS = 300
const DEFAULT_PAGE_SIZE = 25

export interface CampaignSummaryTile {
  key: string
  label: string
  value: string
  note: string
  /** The waiting-on-us figure carries the attention ink; the rest read plain. */
  attention?: boolean
}

export interface CampaignTab {
  key: CampaignTabKey
  label: string
  count: number
}

export interface UseCampaignPageResult {
  /** The service answered 403 — the viewer's role has no campaign access. */
  isForbidden: boolean
  rows: CampaignRow[]
  isLoading: boolean
  summary: CampaignSummaryTile[]
  isSummaryLoading: boolean
  tabs: CampaignTab[]
  activeTab: CampaignTabKey
  onTabChange: (tab: CampaignTabKey) => void
  search: string
  onSearchChange: (value: string) => void
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  rangeFrom: number
  rangeTo: number
  total: number
  onRangeChange: (from: number, to: number) => void
  pagination: DataTablePagination
}

const SUMMARY_TILE_ORDER = [
  'waitingOnUs',
  'runningNow',
  'booked',
  'margin',
  'sendLimits',
] as const

type SummaryTileKey = (typeof SUMMARY_TILE_ORDER)[number]

const SUMMARY_FIELDS: Record<SummaryTileKey, keyof CampaignSummaryRead> = {
  waitingOnUs: 'waiting_on_us',
  runningNow: 'running_now',
  booked: 'booked',
  margin: 'margin',
  sendLimits: 'send_limits',
}

/** The figures above the table, in the order the design's strip reads them. */
function useSummaryTiles(): {
  tiles: CampaignSummaryTile[]
  isLoading: boolean
  isForbidden: boolean
} {
  const { t } = useTranslation()
  const query = useCampaignsSummaryApiV1CampaignsSummaryGet()
  const summary = query.data?.data
  const tiles = useMemo(() => {
    if (summary == null) return []
    return SUMMARY_TILE_ORDER.map((key) => {
      const stat: CampaignStatFigure = toCampaignStat(
        summary[SUMMARY_FIELDS[key]],
      )
      return {
        key,
        label: t(`campaigns.summary.${key}`),
        value: stat.value,
        note: stat.note ?? '',
        // The service tones each figure; only the alert ink changes the tile.
        attention: stat.tone === CampaignTone.attention,
      }
    })
  }, [t, summary])
  return {
    tiles,
    isLoading: query.isPending,
    isForbidden: isPermissionDenied(query.error),
  }
}

/**
 * Page state for the campaign index.
 *
 * The tab, page and page size go to `GET /campaigns/rows`; the tab counts
 * come back on every response whatever tab is active, so the strip never
 * fetches per tab. The toolbar's hundred-row window and the table's page are
 * folded into one server page — the window sizes divide evenly, so the fold is
 * exact. Search goes to the service as `q`, debounced.
 */
export function useCampaignPage(): UseCampaignPageResult {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] =
    useState<CampaignTabKey>(CAMPAIGN_DEFAULT_TAB)
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [rangeFrom, setRangeFrom] = useState(1)

  const summary = useSummaryTiles()

  const serverPage = Math.floor((rangeFrom - 1) / pageSize) + page
  const query = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS)
  const rowsQuery = useListCampaignRowsApiV1CampaignsRowsGet(
    {
      tab: activeTab,
      page: serverPage,
      size: pageSize,
      q: query === '' ? undefined : query,
    },
    { query: { placeholderData: (previous) => previous } },
  )
  const data = rowsQuery.data?.data
  const total = data?.total ?? 0
  const tabCounts = data?.tab_counts

  const tabs = useMemo(
    () =>
      CAMPAIGN_TAB_ORDER.map((key) => ({
        key,
        label: t(campaignTabLabelKey(key)),
        count: tabCounts?.[key] ?? 0,
      })),
    [t, tabCounts],
  )

  const rows = useMemo(() => (data?.rows ?? []).map(toCampaignRow), [data])

  const rangeTo = Math.min(rangeFrom + WINDOW_SIZE - 1, Math.max(total, 1))

  const onRangeChange = useCallback((from: number) => {
    setRangeFrom(from)
    setPage(1)
  }, [])

  const windowItems = Math.max(0, Math.min(total, rangeTo) - rangeFrom + 1)
  const pagination: DataTablePagination = {
    page,
    pageSize,
    totalItems: windowItems,
    totalPages: Math.max(1, Math.ceil(windowItems / pageSize)),
    onPageChange: setPage,
    onPageSizeChange: (size) => {
      setPageSize(size)
      setPage(1)
    },
    window: {
      from: rangeFrom,
      to: rangeTo,
      total,
      hasPrev: rangeFrom > 1,
      hasNext: rangeTo < total,
      onPrev: () => onRangeChange(Math.max(1, rangeFrom - WINDOW_SIZE)),
      onNext: () => onRangeChange(rangeFrom + WINDOW_SIZE),
    },
  }

  useEffect(() => {
    setPage(1)
    setRangeFrom(1)
  }, [query])

  const onTabChange = useCallback((tab: CampaignTabKey) => {
    setActiveTab(tab)
    setPage(1)
    setRangeFrom(1)
    setSelectedIds([])
  }, [])

  return {
    isForbidden: summary.isForbidden || isPermissionDenied(rowsQuery.error),
    rows,
    isLoading: rowsQuery.isPending,
    summary: summary.tiles,
    isSummaryLoading: summary.isLoading,
    tabs,
    activeTab,
    onTabChange,
    search,
    onSearchChange: setSearch,
    selectedIds,
    onSelectionChange: setSelectedIds,
    rangeFrom,
    rangeTo,
    total,
    onRangeChange,
    pagination,
  }
}
